import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  queryKeys,
  ZOZNAM_GC_MS,
  type CennikFacetyFilters,
  type CennikFilters,
  type CennikRozbalovackaParams,
} from "@/api/queryKeys";
import type { components } from "@/api/types.gen";
import type { DruhMaterialu, Sekcia } from "@/api/schemas";
import type {
  CennikFacety,
  DataResponse,
  DodavatelItem,
  EdgeCandidates,
  EdgesForMaterial,
  Kategorie,
  ListResponse,
  Material,
} from "@/api/schemas";

type MaterialCreate = components["schemas"]["MaterialCreateRequest"];
type MaterialUpdate = components["schemas"]["MaterialUpdateRequest"];

/**
 * Search/filter cez cenník (server-side pagination).
 *
 * `zachovajPredosle` — tabuľka Cenníka (KOLO 225): pri zmene strany,
 * hľadania či filtra ostanú staré riadky, kým prídu nové, a zoznam si
 * appka pamätá 30 min. Ostatné volania (návrhy, predvolené kovanie) bez
 * toho — tam by staré riadky patrili inej otázke.
 */
export function useCennikMaterialy(
  filters: CennikFilters = {},
  options: { enabled?: boolean; zachovajPredosle?: boolean } = {},
): UseQueryResult<ListResponse<Material>> {
  return useQuery({
    queryKey: queryKeys.cennik.materialy(filters),
    queryFn: async () => {
      const { data } = await api.get<ListResponse<Material>>(
        "/cennik/materialy",
        { params: filters },
      );
      return data;
    },
    enabled: options.enabled,
    ...(options.zachovajPredosle
      ? { placeholderData: keepPreviousData, gcTime: ZOZNAM_GC_MS }
      : {}),
  });
}

/**
 * Ten istý zoznam, ale po stranách — pre picker, ktorý musí ukázať
 * **celý** cenník, nie prvých dvadsať (KOLO 120).
 *
 * User: *„dávaš mi na výber len nejaké, nie všetky."* `useCennikMaterialy`
 * `page` nikdy neposielal, takže zoznam končil na 20 (prehliadanie)
 * resp. 30 (hľadanie) riadkoch a ďalej sa nedalo dostať.
 *
 * Stránkuje sa, nevirtualizuje: `@tanstack/react-query` v projekte už
 * je, virtualizačná knižnica nie a ťažké závislosti sa nepridávajú bez
 * dohody (CLAUDE.md).
 */
export function useCennikMaterialyStrankovane(
  filters: CennikFilters = {},
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<InfiniteData<ListResponse<Material>>> {
  return useInfiniteQuery({
    queryKey: queryKeys.cennik.materialyStrankovane(filters),
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<ListResponse<Material>>(
        "/cennik/materialy",
        { params: { ...filters, page: pageParam } },
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (posledna) => {
      const { page, total_pages } = posledna.pagination;
      return page < total_pages ? page + 1 : undefined;
    },
    enabled: options.enabled,
    // Pri písaní ostanú riadky predošlého hľadania, kým prídu nové —
    // výber neblikne na „Načítavam…" po každom písmene (KOLO 225)
    placeholderData: keepPreviousData,
  });
}

/**
 * Počty pre rozbaľovačky a záložky Cenníka (KOLO 166) — tie isté filtre
 * ako zoznam. `keepPreviousData`: pri zmene filtra ostanú staré počty,
 * kým neprídu nové, takže rozbaľovačka neblikne na prázdno.
 */
export function useCennikFacety(
  filters: CennikFacetyFilters,
  options: { enabled?: boolean } = {},
): UseQueryResult<DataResponse<CennikFacety>> {
  return useQuery({
    queryKey: queryKeys.cennik.facety(filters),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<CennikFacety>>(
        "/cennik/facety",
        { params: filters },
      );
      return data;
    },
    enabled: options.enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Kovanie pre dielenský kód (`56-3550`, `71B3650`) — u preferovaného
 * dodávateľa (KOLO 166). Rozhoduje server (`/cennik/kovanie-podla-kodu`):
 * číslo artikla dodávateľa alebo kód výrobcu, potom dvojička
 * preferovaného dodávateľa. Do KOLA 166 to bol fulltext s prvým riadkom
 * podľa kategórie a názvu — pri `71B3650` náhodne Demos či Nábytkár.
 * Vracia null, kým je lookup vypnutý alebo cenník kód nemá.
 */
export function useKovaniePodlaKodu(kod: string, enabled: boolean): Material | null {
  const q = useQuery({
    queryKey: queryKeys.cennik.kovaniePodlaKodu(kod),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Material | null>>(
        "/cennik/kovanie-podla-kodu",
        { params: { kod } },
      );
      return data;
    },
    enabled: enabled && !!kod,
  });
  return enabled ? (q.data?.data ?? null) : null;
}

/** Detail jedného materiálu. */
export function useMaterialDetail(
  id: string | undefined,
): UseQueryResult<DataResponse<Material>> {
  return useQuery({
    queryKey: queryKeys.cennik.material(id ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Material>>(
        `/cennik/materialy/${id}`,
      );
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Auto-mapping hrán (tenká + hrubá) pre daný materiál.
 * Frontend volá pri zmene material_id v slot dialógu — UI preview.
 */
export function useEdgesForMaterial(
  materialId: string | undefined,
): UseQueryResult<DataResponse<EdgesForMaterial>> {
  return useQuery({
    queryKey: queryKeys.cennik.edgesFor(materialId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<EdgesForMaterial>>(
        `/cennik/edges-for-material/${materialId}`,
      );
      return data;
    },
    enabled: !!materialId,
  });
}

/**
 * Všetky kandidátne hrany pre materiál (všetky šírky/hrúbky dekoru) +
 * auto-default tenká/hrubá. Slot dialóg ich zobrazí na ručné prepnutie.
 */
export function useEdgeCandidates(
  materialId: string | undefined,
  hrubaHranaMm?: string,
  slotId?: string,
): UseQueryResult<DataResponse<EdgeCandidates>> {
  return useQuery({
    queryKey: queryKeys.cennik.edgeCandidates(
      materialId ?? "",
      hrubaHranaMm,
      slotId,
    ),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (hrubaHranaMm) params.hruba_hrana_mm = hrubaHranaMm;
      if (slotId) params.slot_id = slotId;
      const { data } = await api.get<DataResponse<EdgeCandidates>>(
        `/cennik/edge-candidates/${materialId}`,
        { params },
      );
      return data;
    },
    enabled: !!materialId,
  });
}

/**
 * Distinct hrúbky dosiek (z_mm) — pre voľbu hrúbky v slot dialógu.
 * Default ``druh=plosny``, ``include_daex`` ako picker (false).
 */
export function useHrubkyDosiek(
  druh?: DruhMaterialu,
  includeDaex = false,
): UseQueryResult<DataResponse<string[]>> {
  return useQuery({
    queryKey: queryKeys.cennik.hrubkyDosiek(druh, includeDaex),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<string[]>>(
        "/cennik/hrubky-dosiek",
        { params: { druh, include_daex: includeDaex } },
      );
      return data;
    },
  });
}

/** Trieda šírky hrany (KOLO 222) — hranice drží backend. */
export type SirkaTrieda = NonNullable<CennikFilters["sirka_trieda"]>;

/** Reprezentácia jednej riadku z /api/cennik/edge-widths. */
export interface EdgeWidthOption {
  sirka_mm: number;
  pasmo_kod: SirkaTrieda;
  pasmo_label: string;
  pocet_hran: number;
}

/** Distinct šírky hrán z cenníka + pásmo + počet hrán. */
export function useEdgeWidths(
  options: { enabled?: boolean } = {},
): UseQueryResult<DataResponse<EdgeWidthOption[]>> {
  return useQuery({
    queryKey: [...queryKeys.cennik.all, "edge-widths"] as const,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<EdgeWidthOption[]>>(
        "/cennik/edge-widths",
      );
      return data;
    },
    enabled: options.enabled,
    staleTime: 60_000,
  });
}

/**
 * Len zadané parametre — kľúč dopytu bez parametrov je potom `{}`
 * a zhodný pre každé volanie bez nich (MaterialSelect, dialógy).
 */
function rozbalovackaParams(
  params: CennikRozbalovackaParams | undefined,
): CennikRozbalovackaParams {
  const out: CennikRozbalovackaParams = {};
  if (params?.sekcia) out.sekcia = params.sekcia;
  if (params?.druh) out.druh = params.druh;
  return out;
}

/**
 * Kategórie zoskupené podľa druhu (pre filter dropdown).
 *
 * `sekcia`/`druh` (KOLO 164) zúžia zoznam na záložku Cenníka — pravidlo,
 * kam kategória patrí, drží backend, frontend už prefixy nepočíta.
 * `polozky` nesú druh, sekciu, skupinu kovania a počet.
 */
export function useKategorie(
  params?: CennikRozbalovackaParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<DataResponse<Kategorie>> {
  const p = rozbalovackaParams(params);
  return useQuery({
    queryKey: queryKeys.cennik.kategorie(p),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Kategorie>>(
        "/cennik/kategorie",
        { params: p },
      );
      return data;
    },
    enabled: options.enabled,
    staleTime: 5 * 60 * 1000, // 5 min — kategórie sa menia zriedka
  });
}

/**
 * Dodávatelia (pre filter dropdown) — od KOLA 164 objekty `{kod, nazov,
 * pocet}`: filter posiela `kod`, používateľ vidí `nazov` („TWD (Blum)").
 */
export function useDodavatelia(
  params?: CennikRozbalovackaParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<DataResponse<DodavatelItem[]>> {
  const p = rozbalovackaParams(params);
  return useQuery({
    queryKey: queryKeys.cennik.dodavatelia(p),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<DodavatelItem[]>>(
        "/cennik/dodavatelia",
        { params: p },
      );
      return data;
    },
    enabled: options.enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Zdrojové cenníky dodávateľov (Nastavenia) ──────────────

export type ZdrojCennika = components["schemas"]["ZdrojCennikaItem"];
export type ZdrojImportResult = components["schemas"]["ZdrojImportResult"];
export type ZdrojNahlad = components["schemas"]["ZdrojNahladResponse"];

/**
 * Import celého cenníka beží na serveri aj minúty (Häfele ~40k položiek)
 * — predvolených 30 s klienta by požiadavku zrušilo, kým server ešte
 * pracuje. Náhľad aj potvrdenie preto čakajú dlhšie (KOLO 165).
 */
const IMPORT_CENNIKA_TIMEOUT_MS = 15 * 60 * 1000;
export type UpravaCienResult = components["schemas"]["UpravaCienResult"];

/** Zoznam zdrojových cenníkov — súbor, veľkosť, dátum poslednej verzie. */
export function useZdrojeCennikov(): UseQueryResult<
  DataResponse<ZdrojCennika[]>
> {
  return useQuery({
    queryKey: [...queryKeys.cennik.all, "zdroje"] as const,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZdrojCennika[]>>(
        "/cennik/import/zdroje",
      );
      return data;
    },
  });
}

/**
 * Náhľad nahratia cenníka (KOLO 165) — server spustí ten istý import ako
 * potvrdenie a vráti ho späť: čo pribudne, čomu sa zmení cena, čo zmizne.
 * Nič sa nezapíše, preto nič neinvaliduje.
 */
export function useNahladZdroja(): UseMutationResult<
  ZdrojNahlad,
  unknown,
  { source: string; file: File; zrusitUpravu?: boolean }
> {
  return useMutation({
    mutationFn: async ({ source, file, zrusitUpravu }) => {
      const form = new FormData();
      form.append("file", file);
      if (zrusitUpravu) form.append("zrusit_upravu", "true");
      const { data } = await api.post<DataResponse<ZdrojNahlad>>(
        `/cennik/import/zdroje/${source}/nahlad`,
        form,
        { timeout: IMPORT_CENNIKA_TIMEOUT_MS },
      );
      return data.data;
    },
  });
}

/**
 * Upload novej verzie cenníka dodávateľa + re-import cien.
 *
 * `zrusitUpravu` — nový cenník už zdraženie obsahuje: server najprv zruší
 * % úpravu tohto cenníka (KOLO 168; ostatné cenníky dodávateľa si ju
 * nechajú), inak by ju import uplatnil druhýkrát (KOLO 165).
 * `kontrolaChybajucich` — odtlačok z náhľadu: s ním server položky, ktoré
 * z cenníka zmizli, vyradí (nepoužité) alebo označí „nie je v cenníku"
 * (použité v zákazke). Keď sa od náhľadu zmenili, vráti 409.
 */
export function useUploadZdroj(): UseMutationResult<
  ZdrojImportResult,
  unknown,
  {
    source: string;
    file: File;
    zrusitUpravu?: boolean;
    kontrolaChybajucich?: string;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ source, file, zrusitUpravu, kontrolaChybajucich }) => {
      const form = new FormData();
      form.append("file", file);
      if (zrusitUpravu) form.append("zrusit_upravu", "true");
      if (kontrolaChybajucich) {
        form.append("kontrola_chybajucich", kontrolaChybajucich);
      }
      // Content-Type sa nenastavuje — rieši to interceptor v `client.ts`
      // pre všetky FormData uploady naraz (a nechá ho doplniť prehliadač
      // aj s boundary, ktoré tu doplniť nevieme)
      const { data } = await api.post<DataResponse<ZdrojImportResult>>(
        `/cennik/import/zdroje/${source}`,
        form,
        { timeout: IMPORT_CENNIKA_TIMEOUT_MS },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
    },
  });
}

/**
 * Percentuálna úprava nákupných cien cenníka dodávateľa (±%).
 *
 * `zdroj` (KOLO 168) — cenník (`material.zdroj`), `""` = položky
 * dodávateľa bez cenníka; bez neho všetky jeho cenníky.
 */
export function useUpravaCienDodavatela(): UseMutationResult<
  UpravaCienResult,
  unknown,
  { dodavatel: string; percent: string; zdroj?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dodavatel, percent, zdroj }) => {
      const { data } = await api.post<DataResponse<UpravaCienResult>>(
        `/cennik/dodavatelia/${encodeURIComponent(dodavatel)}/uprava-cien`,
        zdroj === undefined ? { percent } : { percent, zdroj },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
    },
  });
}

export type UpravaCienStavItem =
  components["schemas"]["UpravaCienStavItem"];

/** Aktuálne aplikované % úpravy cien per cenník dodávateľa. */
export function useUpravaCienStav(): UseQueryResult<
  DataResponse<UpravaCienStavItem[]>
> {
  return useQuery({
    queryKey: [...queryKeys.cennik.all, "uprava-cien-stav"] as const,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<UpravaCienStavItem[]>>(
        "/cennik/dodavatelia/uprava-cien-stav",
      );
      return data;
    },
  });
}

export type UpravaCienZaznamItem =
  components["schemas"]["UpravaCienZaznamItem"];

/**
 * Trvalá história % úprav cien (najnovšie prvé).
 *
 * `zdroj` = len záznamy toho cenníka; záznamy spred KOLA 168 cenník
 * nemajú, preto Nastavenia filtrujú len podľa dodávateľa.
 */
export function useUpravaCienHistoria(
  dodavatel?: string,
  zdroj?: string,
): UseQueryResult<DataResponse<UpravaCienZaznamItem[]>> {
  return useQuery({
    queryKey: [
      ...queryKeys.cennik.all,
      "uprava-cien-historia",
      dodavatel ?? "",
      zdroj ?? null,
    ] as const,
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (dodavatel) params.dodavatel = dodavatel;
      if (zdroj !== undefined) params.zdroj = zdroj;
      const { data } = await api.get<DataResponse<UpravaCienZaznamItem[]>>(
        "/cennik/dodavatelia/uprava-cien-historia",
        { params: Object.keys(params).length ? params : undefined },
      );
      return data;
    },
  });
}

export type UpravaCienSkupina = components["schemas"]["UpravaCienSkupinaItem"];

/**
 * Cenníky dodávateľov, ktorým sa dá upraviť cena (KOLO 168) — počet
 * položiek a aktívna úprava; `zdroj` `""` = položky bez cenníka.
 */
export function useUpravaCienSkupiny(): UseQueryResult<
  DataResponse<UpravaCienSkupina[]>
> {
  return useQuery({
    queryKey: queryKeys.cennik.upravaCienSkupiny(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<UpravaCienSkupina[]>>(
        "/cennik/dodavatelia/uprava-cien-skupiny",
      );
      return data;
    },
  });
}

/**
 * Zruší aplikovanú % úpravu cien cenníka (inverzné násobenie).
 *
 * `zdroj` ako pri úprave — `""` = položky bez cenníka, bez neho všetky
 * cenníky dodávateľa.
 */
export function useZrusUpravuCien(): UseMutationResult<
  UpravaCienResult,
  unknown,
  { dodavatel: string; zdroj?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dodavatel, zdroj }) => {
      const { data } = await api.post<DataResponse<UpravaCienResult>>(
        `/cennik/dodavatelia/${encodeURIComponent(dodavatel)}/uprava-cien/zrusit`,
        undefined,
        { params: zdroj === undefined ? undefined : { zdroj } },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
    },
  });
}

/** Distinct výrobcovia/kolekcie — voliteľne len sekcie/druhu (záložky). */
export function useVyrobcovia(
  params?: CennikRozbalovackaParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<DataResponse<string[]>> {
  const p = rozbalovackaParams(params);
  return useQuery({
    queryKey: queryKeys.cennik.vyrobcovia(p),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<string[]>>(
        "/cennik/vyrobcovia",
        { params: p },
      );
      return data;
    },
    enabled: options.enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── CRUD mutations (R1-FINAL) ──────────────────────────────

/** POST /api/cennik/materialy — pridaj nový materiál ručne. */
export function useCreateMaterial(): UseMutationResult<
  DataResponse<Material>,
  Error,
  MaterialCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MaterialCreate) => {
      const { data } = await api.post<DataResponse<Material>>(
        "/cennik/materialy",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
    },
  });
}

export interface MaterialSetCreate {
  nazov: string;
  component_ids: string[];
  /** Kusy každého komponentu v sete, v poradí `component_ids` (KOLO 228). */
  pocty?: number[];
  kategoria?: string | null;
}

/** POST /api/cennik/materialy/set — spoj kovania do setu (práca raz). */
export function useCreateMaterialSet(): UseMutationResult<
  DataResponse<Material>,
  Error,
  MaterialSetCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MaterialSetCreate) => {
      const { data } = await api.post<DataResponse<Material>>(
        "/cennik/materialy/set",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
    },
  });
}

/**
 * PATCH /api/cennik/materialy/{id}/set — uprav zloženie/názov setu.
 * Backend prepočíta cenu = súčet nových komponentov (réžia/práca z hlavného).
 */
export function useUpdateMaterialSet(): UseMutationResult<
  DataResponse<Material>,
  Error,
  { id: string } & MaterialSetCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.patch<DataResponse<Material>>(
        `/cennik/materialy/${id}/set`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
    },
  });
}

/** Položka rebríčka najpoužívanejších kovaní (agregát zo zákaziek). */
export type KovaniePopularneItem =
  components["schemas"]["KovaniePopularneItem"];

/** GET /api/cennik/kovania-popularne — najpoužívanejšie kovania zo zákaziek. */
export function useKovaniaPopularne(
  limit = 15,
): UseQueryResult<DataResponse<KovaniePopularneItem[]>> {
  return useQuery({
    queryKey: [...queryKeys.cennik.all, "kovania-popularne", limit] as const,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<KovaniePopularneItem[]>>(
        "/cennik/kovania-popularne",
        { params: { limit } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/** PATCH /api/cennik/materialy/{id}. */
export function useUpdateMaterial(): UseMutationResult<
  DataResponse<Material>,
  Error,
  { id: string; data: MaterialUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<Material>>(
        `/cennik/materialy/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
      // Ceny zákaziek sa počítajú živo z cenníka — po zmene ceny
      // materiálu treba prepočítať aj zákazky a pravé súhrny (inak až po F5).
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.all });
    },
  });
}


export type HromadnaZmena = components["schemas"]["HromadnaZmena"];
export type HromadnaZmenaResult = components["schemas"]["HromadnaZmenaResult"];

/**
 * Hromadná úprava (KOLO 166): zaškrtnuté riadky (`ids`, najviac 500)
 * alebo všetky podľa filtra — ten istý objekt filtrov ako zoznam, bez
 * stránkovania. `ocakavany_pocet` = koľko user potvrdil; keď server
 * napočíta iný počet, vráti 409 a nezmení nič.
 */
export type HromadnaZmenaPayload = {
  zmena: Partial<HromadnaZmena>;
  ocakavany_pocet: number;
} & ({ ids: string[] } | { filtre: CennikFacetyFilters });

/** Mení hromadná zmena marže (a tým ceny zákaziek)? */
export function meniMarze(zmena: Partial<HromadnaZmena>): boolean {
  return (
    zmena.rezia_pct != null ||
    zmena.praca_eur_per_mj != null ||
    zmena.nadmiera_pct != null ||
    zmena.marze_podla_kategorie === true ||
    zmena.prevziat_marze === true
  );
}

/** POST /api/cennik/materialy/hromadne. */
export function useHromadnaZmena(): UseMutationResult<
  DataResponse<HromadnaZmenaResult>,
  Error,
  HromadnaZmenaPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: HromadnaZmenaPayload) => {
      const { data } = await api.post<DataResponse<HromadnaZmenaResult>>(
        "/cennik/materialy/hromadne",
        payload,
      );
      return data;
    },
    // Aj pri chybe — 409 znamená, že zoznam je zastaraný, počty treba
    // načítať znova. Marže menia ceny zákaziek (počítajú sa živo
    // z cenníka), kategória bez nich, výrobca ani vyradenie nie.
    onSettled: (_data, _err, payload) => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
      if (meniMarze(payload.zmena)) {
        qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
        qc.invalidateQueries({ queryKey: queryKeys.varianty.all });
      }
    },
  });
}

/** DELETE /api/cennik/materialy/{id} — soft delete (je_aktivny=false). */
export function useDeleteMaterial(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/cennik/materialy/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
    },
  });
}

// ─── Nastavenia cien — marže kategórií (KOLO 166) ────────────

/**
 * Riadok GET /api/cennik/marze-kategorii — kategória v jednom druhu
 * (`KOVANIE_PROFIL` €/ks aj €/m sú dva riadky). Čísla sú tie, ktoré dá
 * kategórii import aj nová položka; sklo server nevracia.
 */
export type KategoriaMarzeItem = components["schemas"]["KategoriaMarzeItem"];
export type MarzeKategoriiResult = components["schemas"]["MarzeKategoriiResult"];

/** Pole marže, ktoré Nastavenia cien menia. */
export type PoleMarze =
  | "praca_eur_per_mj"
  | "rezia_pct"
  | "nadmiera_pct"
  | "nadmiera_smer_pct";

/**
 * Body PUT /api/cennik/marze-kategorii — výslovné páry (druh, kategória)
 * a len polia, ktoré sa menia (chýbajúce ostanú). Čísla ako text
 * s bodkou, nech sa nestratí presnosť.
 */
export type MarzeKategoriiPayload = {
  polozky: { druh: DruhMaterialu; kategoria: string }[];
  aj_vlastne?: boolean;
} & Partial<Record<PoleMarze, string>>;

/** Marže všetkých kategórií pre panel Nastavenia cien. */
export function useMarzeKategorii(): UseQueryResult<
  DataResponse<KategoriaMarzeItem[]>
> {
  return useQuery({
    queryKey: queryKeys.cennik.marzeKategorii(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<KategoriaMarzeItem[]>>(
        "/cennik/marze-kategorii",
      );
      return data;
    },
  });
}

/**
 * Ulož marže kategóriám — server ich zapíše do appky (prežijú import
 * cenníka) a zarovná na ne položky. Ceny zákaziek sa počítajú živo
 * z cenníka, preto aj zákazky a varianty.
 */
export function useUlozMarzeKategorii(): UseMutationResult<
  DataResponse<MarzeKategoriiResult>,
  Error,
  MarzeKategoriiPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MarzeKategoriiPayload) => {
      const { data } = await api.put<DataResponse<MarzeKategoriiResult>>(
        "/cennik/marze-kategorii",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.all });
    },
  });
}

// ─── Preferovaní dodávatelia (KOLO 166) ──────────────────────

/** Značka s kódom výrobcu a jej preferovaný dodávateľ (Nastavenia). */
export type ZnackaPrehlad = components["schemas"]["ZnackaPrehladItem"];

/**
 * Značky s kódom výrobcu, koľko riadkov má ktorý dodávateľ a koľko by
 * ich výbery skryli pri každej voľbe — GET /cennik/preferovani-dodavatelia.
 */
export function usePreferovaniDodavatelia(): UseQueryResult<
  DataResponse<ZnackaPrehlad[]>
> {
  return useQuery({
    queryKey: queryKeys.cennik.preferovaniDodavatelia(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZnackaPrehlad[]>>(
        "/cennik/preferovani-dodavatelia",
      );
      return data;
    },
  });
}

/**
 * Nastav (`dodavatel`) alebo zruš (`null`) preferenciu značky. Mení, čo
 * ponúkajú pickery a štítky v Cenníku — preto celý cenník; zákazky nie,
 * uložené položky sa nepresmerujú.
 */
export function useNastavPreferovanehoDodavatela(): UseMutationResult<
  DataResponse<ZnackaPrehlad[]>,
  Error,
  { znacka: string; dodavatel: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ znacka, dodavatel }) => {
      const { data } = await api.put<DataResponse<ZnackaPrehlad[]>>(
        `/cennik/preferovani-dodavatelia/${encodeURIComponent(znacka)}`,
        { dodavatel },
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.cennik.preferovaniDodavatelia(), data);
      qc.invalidateQueries({ queryKey: queryKeys.cennik.all });
    },
  });
}

// ─── Štruktúry povrchu (katalóg) ─────────────────────────────

/**
 * Štruktúra povrchu z GET /api/cennik/struktury.
 *
 * Identita je (kód, výrobca) — ten istý kód môžu mať dvaja výrobcovia
 * (KOLO 168, katalóg v DB). `id` chýba pri predvoľbách (prázdny katalóg)
 * a pri „Nezaradených" kódoch z cenníka; `ma_smer` = štruktúra kreslí
 * drevo, import dá doske smer vlákien X.
 */
export type StrukturaItem = components["schemas"]["StrukturaItem"];

/** Response GET /api/cennik/struktury — katalóg + labels typov povrchu. */
export type StrukturyKatalog = components["schemas"]["StrukturyKatalogResponse"];

export type StrukturaCreate = components["schemas"]["StrukturaCreateRequest"];
type StrukturaUpdate = components["schemas"]["StrukturaUpdateRequest"];

/**
 * Ako má nová položka vyzerať — kód aj parametre, ktoré z neho plynú.
 *
 * Práca, réžia a nadmiera sú v `pricing_rules.yaml` **per kategória**
 * a kategória plošnej dosky je daná hrúbkou; formát je pri DTD skoro
 * vždy ten istý. Pýtať sa na to ručne znamená len možnosť pomýliť sa.
 */
export interface NavrhMaterialu {
  kod: string;
  /** Skladá ho appka? `false` = dodávateľ má vlastné číslo artikla. */
  skladany: boolean;
  napoveda: string;
  existuje_id: string | null;
  existuje_nazov: string | null;
  /** Odvodená kategória — z nej plynú marže. */
  kategoria: string | null;
  rezia_pct: string | null;
  praca_eur_per_mj: string | null;
  nadmiera_pct: string | null;
  /** Najčastejší formát dosky v cenníku pre túto kategóriu. */
  x_mm: number | null;
  y_mm: number | null;
  /** Čo dekor v cenníku už má — aby nevznikol v inej štruktúre omylom. */
  struktury_dekoru: { kod: string; hrubky: string[] }[];
  /**
   * Smer vlákien dekorovej dosky tým istým pravidlom ako import (KOLO 168);
   * `null` = netýka sa (drevené typy, hrany, kovanie).
   */
  smer_navrh?: "X" | "0" | null;
  /**
   * Hrana Hranipexu: čo cenník o propagačnej skupine už vie (KOLO 169) —
   * sprievodca z toho predvyplní prázdne polia.
   */
  skupina?: components["schemas"]["SkupinaHranipex"] | null;
}

export interface NavrhMaterialuParams {
  druh: DruhMaterialu;
  /** Čo sa pridáva (KOLO 169): kovanie má kód dodávateľa, ostatné skladaný. */
  sekcia?: Sekcia;
  dodavatel?: string;
  dekor?: string;
  z_mm?: string;
  struktura?: string;
  sirka_mm?: string;
  hrubka_mm?: string;
  kategoria_hint?: string;
  /** "X" = drevná kresba → nadmiera so smerom (30 %), "0" = uni (KOLO 166). */
  smer_vlakien?: string;
  /** Typ plošnej dosky (`useTypyDosiek`); bez neho dekorová doska (KOLO 167). */
  typ_dosky?: string;
  /** Drevina, farba alebo variant — pole za typom v kóde. */
  zaklad?: string;
  /** Kvalita alebo povrch — pole za hrúbkou v kóde. */
  povrch?: string;
  /** Formát drevených dosiek je súčasť kódu (každý formát = iná položka). */
  x_mm?: string;
  y_mm?: string;
  /** Výrobca a názov — pre návrh smeru vlákien (štruktúra výrobcu, drevina). */
  vyrobca?: string;
  nazov?: string;
  /** Hrana Hranipexu — propagačná skupina `HD 211381` (+ „lep.“) — KOLO 169. */
  predpona?: string;
  cislo?: string;
  lepidlo?: string;
}

/**
 * Typ plošnej dosky — z čoho sa skladá kód a kam doska patrí (KOLO 167).
 *
 * Register žije na serveri (`services.typy_dosiek`); sprievodca podľa neho
 * len ukáže polia, kód skladá `/navrh-materialu`.
 */
export type TypDosky = components["schemas"]["TypDoskyResponse"];

export function useTypyDosiek(enabled = true): UseQueryResult<DataResponse<TypDosky[]>> {
  return useQuery({
    queryKey: queryKeys.cennik.typyDosiek(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<TypDosky[]>>("/cennik/typy-dosiek");
      return data;
    },
    // Register sa mení len s nasadením
    staleTime: Infinity,
    enabled,
  });
}

/**
 * Kód aj marže skladá **backend** — importér, migrácia aj sprievodca
 * musia dať ten istý reťazec a marže musia sedieť na import cenníkov;
 * kópia v TS by sa rozišla (lekcia z KOLA 77). Duplicita kódu chodí
 * v tej istej odpovedi.
 */
export interface MarzeKategorie {
  kategoria: string;
  rezia_pct: string;
  praca_eur_per_mj: string;
  nadmiera_pct: string;
}

/**
 * Marže, ktoré `pricing_rules.yaml` predpisuje jednej kategórii (KOLO 98).
 *
 * `useNavrhMaterialu` sa použiť nedá — ten kategóriu **odvodzuje**
 * z hrúbky a rozmerov a `kategoria_hint` mu slúži len na príznak
 * pracovnej dosky. Tu je otázka opačná: kategóriu už poznám a chcem jej
 * čísla, aby sa dali ponúknuť po ručnej oprave zaradenia.
 */
export function useMarzeKategorie(
  druh: DruhMaterialu,
  kategoria: string | null,
  /** Smer vlákien položky — drevná kresba má vyššiu nadmieru (KOLO 166). */
  smerVlakien?: string,
): UseQueryResult<DataResponse<MarzeKategorie>> {
  return useQuery({
    queryKey: queryKeys.cennik.marzeKategorie(druh, kategoria ?? "", smerVlakien ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<MarzeKategorie>>(
        "/cennik/marze-kategorie",
        { params: { druh, kategoria, smer_vlakien: smerVlakien || undefined } },
      );
      return data;
    },
    enabled: !!kategoria,
  });
}

export function useNavrhMaterialu(
  params: NavrhMaterialuParams | null,
): UseQueryResult<DataResponse<NavrhMaterialu>> {
  const cistene = Object.fromEntries(
    Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
  return useQuery({
    queryKey: queryKeys.cennik.navrhMaterialu(cistene),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<NavrhMaterialu>>(
        "/cennik/navrh-materialu",
        { params: cistene },
      );
      return data;
    },
    enabled: params !== null,
  });
}

/** Katalóg povrchových štruktúr (DB + „Nezaradené" kódy z cenníka). */
export function useStruktury(
  options: { enabled?: boolean } = {},
): UseQueryResult<StrukturyKatalog> {
  return useQuery({
    queryKey: queryKeys.cennik.struktury(),
    queryFn: async () => {
      const { data } = await api.get<StrukturyKatalog>("/cennik/struktury");
      return data;
    },
    staleTime: 60_000,
    enabled: options.enabled,
  });
}

/** Výsledok DELETE /api/cennik/struktury/{id} — {ok, kod}. */
export type StrukturaMutationResult = components["schemas"]["StrukturaMutationResult"];

/**
 * POST /api/cennik/struktury — pridaj štruktúru výrobcu do katalógu.
 *
 * Od KOLA 168 sa ukladá do DB (dovtedy žila len do reštartu servera).
 * Tá istá dvojica (kód, výrobca) → 409; `ma_smer` bez zadania podľa typu.
 */
export function useAddStruktura(): UseMutationResult<
  DataResponse<StrukturaItem>,
  Error,
  StrukturaCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<DataResponse<StrukturaItem>>(
        "/cennik/struktury",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.struktury() });
    },
  });
}

/**
 * PATCH /api/cennik/struktury/{id} — názov, typ, popis alebo smer vlákien.
 *
 * Kód a výrobca sú identita a nemenia sa. Smer dosiek podľa zmeny prepočíta
 * až ďalší import cenníka.
 */
export function usePatchStruktura(): UseMutationResult<
  DataResponse<StrukturaItem>,
  Error,
  { id: string; data: StrukturaUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data: telo }) => {
      const { data } = await api.patch<DataResponse<StrukturaItem>>(
        `/cennik/struktury/${encodeURIComponent(id)}`,
        telo,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.struktury() });
    },
  });
}

/**
 * DELETE /api/cennik/struktury/{id} — podľa `id`, nie kódu: `SM` má
 * Egger aj Pfleiderer a mazanie podľa kódu by zobralo oboch.
 */
export function useDeleteStruktura(): UseMutationResult<
  StrukturaMutationResult,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<StrukturaMutationResult>(
        `/cennik/struktury/${encodeURIComponent(id)}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cennik.struktury() });
    },
  });
}
